import { useState } from "react";
import { TableHeading } from "@/components/Table";
import { useSetIndexTabBarActions } from "@/components/Tabs/IndexTabBarActions";
import { useCanManageTasksBool } from "@/hooks/useCapabilities";
import useEffectiveDisplayUser from "@/hooks/useEffectiveDisplayUser";
import useTasksQuery from "../_hooks/useTasksQuery";
import TasksTable from "./TasksTable";
import CreateGlobalTaskSideSheet from "@/layouts/AppLayout/CreateGlobalTask/CreateGlobalTaskSideSheet";
import type { TableAction } from "@/components/Table";
import type { TaskAnalyticsSource } from "../_utils/amplitude";

interface TasksTabContentProps {
  heading?: string;
  body?: string;
  source: TaskAnalyticsSource;
  queryOptions?: {
    fixedDepartment?: string;
    fixedUserId?: string;
    myTasks?: boolean;
    useTeamTagForDepartment?: boolean;
    showUnassignedChip?: boolean;
  };
  hideUserChips?: boolean;
}

export default function TasksTabContent({ heading, body, source, queryOptions, hideUserChips }: TasksTabContentProps) {
  const canManageTasks = useCanManageTasksBool();
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const user = useEffectiveDisplayUser();
  const hasDepartment = !!user?.department;

  const queryState = useTasksQuery(queryOptions);

  const actions: TableAction[] = canManageTasks
    ? [{ label: "Add Task", icon: "add" as const, onClick: () => setAddTaskOpen(true) }]
    : [];
  useSetIndexTabBarActions(hasDepartment ? actions : []);

  function handleClearAllFilters() {
    queryState.clearSearch();
    queryState.setStage(undefined);
    queryState.setDepartment(undefined);
    queryState.setAssignedToMe(false);
    queryState.setCreatedByMe(false);
    queryState.setOverdue(false);
    queryState.setUnassigned(false);
    queryState.setDueBy(undefined);
  }

  return (
    <div className="w-full">
      {heading || body || (!hasDepartment && actions.length > 0) ? (
        <TableHeading heading={heading} body={body} actions={hasDepartment ? [] : actions} />
      ) : (
        <div className="pt-6" />
      )}
      <TasksTable
        {...queryState}
        onClearAllFilters={handleClearAllFilters}
        hideUserChips={hideUserChips}
        source={source}
      />
      <CreateGlobalTaskSideSheet sidebarOpen={addTaskOpen} setSidebarOpen={setAddTaskOpen} source={source} />
    </div>
  );
}
